import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import locationHelperBuilder from 'redux-auth-wrapper/history4/locationHelper';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { connectedRouterRedirect } from 'redux-auth-wrapper/history4/redirect';
import GlobalInfo from 'components/shared/GlobalInfo';

import BookingInfoContainer from 'containers/BookingInfoContainer';
import LogoutContainer from 'containers/LogoutContainer';
import NotFoundContainer from 'containers/NotFoundContainer';
import Startup from 'containers/Startup';
import TokenRedirectContainer from 'containers/TokenRedirectContainer';
import BookingsListContainer from 'modules/bookings/containers/BookingsListContainer';
import BookingsCreateContainer from 'modules/bookings/containers/Create';
import BookingsEditContainer from 'modules/bookings/containers/Edit';
import BookingsEditOfferContainer from 'modules/bookings/containers/EditOffer';
import UserTodoContainer from 'modules/users/containers/todo';
import ChangePwdContainer from 'modules/users/containers/change-password';

import ReviewsListContainer from 'modules/reviews/containers/ReviewsListContainer';
import ReviewsAddContainer from 'modules/reviews/containers/AddContainer';
import ReviewsEditContainer from 'modules/reviews/containers/EditContainer';
import ReceiptContainer from 'modules/payment/containers/ReceiptContainer';
import OrdersListContainer from 'modules/payment/containers/OrdersListContainer';
import ResetPwdContainer from 'modules/auth/containers/ResetPassword';
import NewsContainer from 'modules/news/containers/index';
import ConversationsList from 'modules/conversations/containers/List';
import ConversationView from 'modules/conversations/containers/View';
import ConversationBookings from 'modules/conversations/containers/ViewBookings';
import CottagesList from 'modules/cottages/containers/List';
import CottagesCreate from 'modules/cottages/containers/Create';
import CottagesEdit from 'modules/cottages/containers/Edit';
import CottagesPayment from 'modules/cottages/containers/Payment';
import LoginContainer from 'modules/auth/containers/Login';
import HostFilesContainer from 'modules/hostFiles/containers/HostFilesContainer';
import CottageFilesContainer from 'modules/hostFiles/containers/CottageFilesContainer';

import Profile from 'modules/users/containers/Profile';
import Payout from 'modules/users/containers/Payout';
import SCAEnroll from 'modules/users/containers/SCAEnroll';

import ComplaintFormContainer from 'modules/complaints/containers/ComplaintFormContainer';
import ComplaintsListContainer from 'modules/complaints/containers/ComplaintsListContainer';
import ComplaintDetailContainer from 'modules/complaints/containers/ComplaintDetailContainer';
import ComplaintEditContainer from 'modules/complaints/containers/ComplaintEditContainer';
import HostComplaintsListContainer from 'modules/complaints/containers/HostComplaintsListContainer';
import HostComplaintDetailContainer from 'modules/complaints/containers/HostComplaintDetailContainer';
import PATHS from 'constants/paths';
import { CssBaseline } from '@material-ui/core';

const locationHelper = locationHelperBuilder({
  redirectQueryParamName: 'next',
});
const authenticatedSelector = ({ data }) => !isEmpty(data);
const authenticatingSelector = ({ isLoading }) => isLoading;

const userIsAuth = connectedRouterRedirect({
  redirectPath: '/login',
  authenticatedSelector: ({ user }) => authenticatedSelector(user),
  wrapperDisplayName: 'UserIsAuthenticated',
  authenticatingSelector: ({ user }) => authenticatingSelector(user),
  AuthenticatingComponent: () => null,
  redirectQueryParamName: 'next',
});

const userIsNotAuth = connectedRouterRedirect({
  redirectPath: ({ user }, ownProps) => {
    const next = locationHelper.getRedirectQueryParam(ownProps) || '/conversations';
    return user.data.type === 'SOFT' ? `${PATHS.users.profile}?next=${next}` : next;
  },
  allowRedirectBack: false,
  authenticatedSelector: ({ user }) => !authenticatedSelector(user),
  wrapperDisplayName: 'UserIsNotAuthenticated',
  redirectQueryParamName: 'next',
});

// https://github.com/mjrussell/redux-auth-wrapper/issues/224#issuecomment-375837028
const ResetPasswordWrap = userIsNotAuth(ResetPwdContainer);
const ReceiptContainerWrap = userIsAuth(ReceiptContainer);
const OrdersListContainerWrap = userIsAuth(OrdersListContainer);
const BookingInfoContainerWrap = userIsAuth(BookingInfoContainer);
const BookingsListContainerWrap = userIsAuth(BookingsListContainer);
const ReviewsListContainerWrap = userIsAuth(ReviewsListContainer);
const ReviewsAddContainerWrap = userIsAuth(ReviewsAddContainer);
const ReviewsEditContainerWrap = userIsAuth(ReviewsEditContainer);
const UserTodoContainerWrap = userIsAuth(UserTodoContainer);
const ChangePwdContainerWrap = userIsAuth(ChangePwdContainer);
const ComplaintFormContainerWrap = userIsAuth(ComplaintFormContainer);
const ComplaintEditContainerWrap = userIsAuth(ComplaintEditContainer);
const HostComplaintsListContainerWrap = userIsAuth(HostComplaintsListContainer);
const HostComplaintDetailContainerWrap = userIsAuth(HostComplaintDetailContainer);
const ComplaintsListContainerWrap = userIsAuth(ComplaintsListContainer);
const ComplaintDetailContainerWrap = userIsAuth(ComplaintDetailContainer);
const NewsContainerWrap = userIsAuth(NewsContainer);
const ProfileWrap = userIsAuth(Profile);

const Routes = ({ userData, location }) => (
  <div>
    <CssBaseline />
    {userData.type === 'SOFT' && <GlobalInfo pathname={location.pathname} />}
    <Startup>
      <Switch>
        <Route exact path="/" component={() => <Redirect to={PATHS.conversations.root} />} />
        <Route exact path={PATHS.auth.logout} component={LogoutContainer} />
        <Route exact path={PATHS.auth.login} component={userIsNotAuth(LoginContainer)} />
        <Route exact path={PATHS.auth.resetPassword} component={ResetPasswordWrap} />
        <Route exact path="/orders/:orderId" component={ReceiptContainerWrap} />
        <Route exact path="/orders" component={OrdersListContainerWrap} />
        <Route exact path="/orders/dac7/info" component={OrdersListContainerWrap} />
        <Route exact path={PATHS.cottages.root} component={userIsAuth(CottagesList)} />
        <Route
          exact
          path={`${PATHS.cottages.create}/:dialog?`}
          component={userIsAuth(CottagesCreate)}
        />
        <Route exact path={PATHS.cottages.payment} component={userIsAuth(CottagesPayment)} />
        <Route exact path={PATHS.cottages.editStep} component={userIsAuth(CottagesEdit)} />
        <Route exact path={PATHS.users.profile} component={ProfileWrap} />
        <Route exact path={PATHS.users.payout} component={userIsAuth(Payout)} />
        <Route exact path={PATHS.users.scaEnroll} component={userIsAuth(SCAEnroll)} />
        <Redirect path="/userform" to={PATHS.users.profile} />
        <Route exact path="/change-password" component={ChangePwdContainerWrap} />
        <Route exact path={PATHS.conversations.root} component={userIsAuth(ConversationsList)} />
        <Route exact path={PATHS.conversations.read} component={userIsAuth(ConversationView)} />
        <Route
          exact
          path={PATHS.conversations.bookings}
          component={userIsAuth(ConversationBookings)}
        />
        <Route exact path="/redirect" component={TokenRedirectContainer} />
        <Route
          exact
          path={PATHS.conversations.newBooking}
          component={userIsAuth(BookingsCreateContainer)}
        />
        <Route exact path={PATHS.conversations.booking} component={BookingInfoContainerWrap} />
        <Route exact path={PATHS.bookings.root} component={BookingsListContainerWrap} />
        <Route
          exact
          path={PATHS.conversations.editBooking}
          component={userIsAuth(BookingsEditContainer)}
        />
        <Route
          exact
          path={PATHS.conversations.editBookingOffer}
          component={userIsAuth(BookingsEditOfferContainer)}
        />
        <Route exact path={PATHS.hostFiles.cottage} component={userIsAuth(CottageFilesContainer)} />
        <Route exact path={PATHS.hostFiles.root} component={userIsAuth(HostFilesContainer)} />
        <Route exact path={PATHS.reviews.root} component={ReviewsListContainerWrap} />
        <Route exact path={PATHS.reviews.add} component={ReviewsAddContainerWrap} />
        <Route exact path={PATHS.reviews.edit} component={ReviewsEditContainerWrap} />
        <Route exact path={PATHS.complaints.root} component={ComplaintsListContainerWrap} />
        <Route exact path={PATHS.complaints.new} component={ComplaintFormContainerWrap} />
        <Route exact path={PATHS.complaints.edit} component={ComplaintEditContainerWrap} />
        <Route exact path={PATHS.complaints.detail} component={ComplaintDetailContainerWrap} />
        <Route exact path={PATHS.hostComplaints.root} component={HostComplaintsListContainerWrap} />
        <Route
          exact
          path={PATHS.hostComplaints.detail}
          component={HostComplaintDetailContainerWrap}
        />
        <Route exact path="/to-do" component={UserTodoContainerWrap} />
        <Route exact path="/news/:id" component={NewsContainerWrap} />
        <Route component={NotFoundContainer} />
      </Switch>
    </Startup>
  </div>
);

Routes.propTypes = {
  userData: PropTypes.shape({
    type: PropTypes.string,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

const mapStateToProps = (state) => ({
  userData: state.user.data,
});

export default withRouter(connect(mapStateToProps)(Routes));
